import { IDonor } from '@nkr/models/donor';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    header: {
        flexDirection: 'row',
        textAlign: 'left',
        backgroundColor: 'white',
        borderBottom: '0.5px solid black',
        paddingBottom: 16,
        marginBottom: 10,
    },

    headerCell: {
        flex: 1,
    },

    tableCell: {
        flex: 1,
        padding: 2.5,
        paddingHorizontal: 0,
        color: 'black',
    },

    tableCellLabel: {
        padding: 2.5,
        paddingHorizontal: 0,
        color: 'black',
        fontWeight: 'bold'
    },

    row: {
        flexDirection: 'row',
        backgroundColor: 'white',
        marginVertical: 2.5,
        display: 'flex',
        width: '100%',
        fontSize: 10,
    },
})


interface IProps {
    data: IDonor,
    debug: boolean
}

const DonorInfo = (props: IProps) => {
    const { data, debug } = props;

    const subType = () => {
        if (data.bloodType == 'A') {
            return data.aboASubType.toString();
        } else if (data.bloodType = 'AB') {
            return data.aboAbSubType.toString();
        }

        return '';
    }

    if (data == null || data.hlaData == null) {
        return null;
    }

    const medicalDetails = [
        { col1: { label: 'Blood Type', value: data.bloodType }, col2: { label: 'Kidney', value: data.whichKidney } },
        { col1: { label: 'A or AB Subtype (if applicable)', value: subType }, col2: { label: 'Number of Veins', value: data.numberOfVeins } },
        { col1: { label: 'Birth Date', value: data.phiDOB }, col2: { label: 'Number of Arteries', value: data.numberOfArteries } },
        { col1: { label: 'Height', value: data.height + 'cm' }, col2: { label: 'Number of Ureters', value: data.numberOfUreters } },
        { col1: { label: 'Weight', value: data.weight + 'kg' }, col2: { label: 'Kidney CT/US (height in cm)', value: data.kidneyHeight } },
        { col1: { label: 'Blood Pressure', value: data.bloodPressure }, col2: { label: 'Kidney CT/US (length in cm)', value: data.kidneyLength } },
        { col1: { label: 'Race', value: data.race }, col2: { label: 'Kidney CT/US (width in cm)', value: data.kidneyWidth } },
        { col1: { label: 'Gender', value: data.gender }, col2: { label: 'Kidney 3D-CT (volume in cc)', value: data.kidneyVolume } },
        { col1: { label: 'Creatinine Clearance / GFR', value: data.creatinineClearance }, col2: { label: 'EBV', value: data.ebv } },
        { col1: { label: '24 Hour Urine Protein', value: data.urineProtein }, col2: { label: 'CMV', value: data.cmv } },
        { col1: { label: 'Albumin Excretion', value: data.albuminExcretion }, col2: null },
        { col1: { label: 'Hepatitis B Antigen', value: data.hepBAntigen }, col2: { label: 'Anti-HVC', value: data.antiHcv } },
        { col1: { label: 'Hepatitis B Core Antibody', value: data.hepBAntibody }, col2: { label: 'HIV', value: data.hiv } },
        { col1: { label: 'RPR/VDRL', value: data.rprVdrl }, col2: { label: 'TB Status', value: data.tbStatus } },
    ];

    const hlaDetails = [
        { hla: 'A', lr1: data.hlaData.hlaA1, hr1: data.hlaData.hlaA1HighRes, lr2: data.hlaData.hlaA2, hr2: data.hlaData.hlaA2HighRes },
        { hla: 'B', lr1: data.hlaData.hlaB1, hr1: data.hlaData.hlaB1HighRes, lr2: data.hlaData.hlaB2, hr2: data.hlaData.hlaB2HighRes },
        { hla: 'DR', lr1: data.hlaData.hlaDr1, hr1: data.hlaData.hlaDr1HighRes, lr2: data.hlaData.hlaDr2, hr2: data.hlaData.hlaDr2HighRes },
        { hla: 'Bw', lr1: data.hlaData.hlaBw1, hr1: data.hlaData.hlaBw1HighRes, lr2: data.hlaData.hlaBw2, hr2: data.hlaData.hlaBw2HighRes },
        { hla: 'Cw', lr1: data.hlaData.hlaCw1, hr1: data.hlaData.hlaCw1HighRes, lr2: data.hlaData.hlaCw2, hr2: data.hlaData.hlaCw2HighRes },
        { hla: 'DQ', lr1: data.hlaData.hlaDq1, hr1: data.hlaData.hlaDq1HighRes, lr2: data.hlaData.hlaDq2, hr2: data.hlaData.hlaDq2HighRes },
        { hla: 'DQa', lr1: data.hlaData.hlaDqa1, hr1: data.hlaData.hlaDqa1HighRes, lr2: data.hlaData.hlaDqa2, hr2: data.hlaData.hlaDqa2HighRes },
        { hla: 'DP', lr1: data.hlaData.hlaDp1, hr1: data.hlaData.hlaDp1HighRes, lr2: data.hlaData.hlaDp2, hr2: data.hlaData.hlaDp2HighRes },
        { hla: 'DR-51', lr1: data.hlaData.hlaDr51 ? 'Y' : 'N', hr1: '', lr2: '', hr2: '' },
        { hla: 'DR-52', lr1: data.hlaData.hlaDr52 ? 'Y' : 'N', hr1: '', lr2: '', hr2: '' },
        { hla: 'DR-53', lr1: data.hlaData.hlaDr53 ? 'Y' : 'N', hr1: '', lr2: '', hr2: '' },
    ]

    return (
        <Document style={{ display: 'flex', width: '90%' }}>
            <Page size='LETTER' orientation='landscape' style={{ fontSize: 12, padding: '10px 20px' }} debug={debug}>
                <View style={{ ...styles.row, marginBottom: 20, fontSize: 18 }}>
                    <View style={{ ...styles.tableCell, width: '50%' }}>
                        <Text style={{ fontWeight: 'bold' }}>NATIONAL KIDNEY REGISTRY</Text>
                    </View>

                    <View style={{ ...styles.tableCell, width: '50%', textAlign: 'right' }}>
                        <Text style={{ fontWeight: 'bold' }}>Donor Record: <Text style={{ fontWeight: 'normal' }}>{data.alias}</Text></Text>
                    </View>
                </View>

                <View style={{ ...styles.row, fontSize: 14, marginBottom: 10 }}>
                    <Text>ADMINISTRATIVE INFORMATION</Text>
                </View>
                <View style={{ ...styles.row, borderTop: '1px solid #ccc' }}>
                    <View style={{ ...styles.tableCell, width: '33%', textAlign: 'center' }}>
                        <Text style={{ fontWeight: 'bold' }}>Registered On: <Text style={{ fontWeight: 'normal' }}>{data.dateRegistered}</Text></Text>
                    </View>
                    <View style={{ ...styles.tableCell, width: '33%', textAlign: 'center' }}>
                        <Text style={{ fontWeight: 'bold' }}>Donor is Active: <Text style={{ fontWeight: 'normal' }}>{data.isActive ? 'Yes' : 'No'}</Text></Text>
                    </View>
                    <View style={{ ...styles.tableCell, width: '33%', textAlign: 'center' }}>
                        <Text style={{ fontWeight: 'bold' }}>Donor is Worked Up: <Text style={{ fontWeight: 'normal' }}>{data.isWorkedUp ? 'Yes' : 'No'}</Text></Text>
                    </View>
                </View>
                <View style={{ ...styles.row, borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
                    <View style={{ ...styles.tableCell, width: '33%', textAlign: 'center' }}>
                        <Text style={{ fontWeight: 'bold' }}>Swap: <Text style={{ fontWeight: 'normal' }}>{data.cluster.chainId}/{data.cluster.clusterNumber}{data.position}</Text></Text>
                    </View>
                    <View style={{ ...styles.tableCell, width: '33%', textAlign: 'center' }}>
                        <Text style={{ fontWeight: 'bold' }}>Status: <Text style={{ fontWeight: 'normal' }}>{data.pairStatus}</Text></Text>
                    </View>
                    <View style={{ ...styles.tableCell, width: '33%', textAlign: 'center' }}>
                        <Text></Text>
                    </View>
                </View>

                <View style={{ ...styles.row, fontSize: 14, marginTop: 20, marginBottom: 10 }}>
                    <Text>MEDICAL INFORMATION</Text>
                </View>

                <View style={{ ...styles.row }}>
                    <View style={{ ...styles.tableCell, width: '100%' }}>
                        <Text style={{ fontWeight: 'bold' }}>Medical Notes and Additional Tests</Text>
                    </View>
                </View>

                <View style={{ ...styles.row }}>
                    <View style={{ ...styles.tableCell, width: '100%' }}>
                        <Text>{data.medicalComments}</Text>
                    </View>
                </View>

                <View style={{ ...styles.row }}>
                    <View style={{ ...styles.tableCell, width: '100%' }}>
                        <Text style={{ fontWeight: 'bold' }}>CT Report</Text>
                    </View>
                </View>

                <View style={{ ...styles.row }}>
                    <View style={{ ...styles.tableCell, width: '100%' }}>
                        <Text>{data.ctReportUploadPath}</Text>
                    </View>
                </View>

                {medicalDetails.map((item, index) =>
                    <View key={'medical-details-' + index} style={{ ...styles.row, borderTop: '0px solid #ccc', borderBottom: '1px solid #ccc' }}>
                        <View style={{ ...styles.tableCellLabel, width: '25%', textAlign: 'center' }}>
                            <Text>{item.col1.label}:</Text>
                        </View>
                        <View style={{ ...styles.tableCell, width: '25%', textAlign: 'center' }}>
                            <Text>{item.col1.value}</Text>
                        </View>
                        <View style={{ ...styles.tableCellLabel, width: '25%', textAlign: 'center' }}>
                            {item.col2 != null && <Text>{item.col2.label}:</Text>}
                        </View>
                        <View style={{ ...styles.tableCell, width: '25%', textAlign: 'center' }}>
                            {item.col2 != null && <Text>{item.col2.value}</Text>}
                        </View>
                    </View>
                )}
            </Page>

            <Page size='LETTER' orientation='landscape' style={{ fontSize: 12, padding: '10px 20px' }} debug={debug}>
                <View style={{ ...styles.row, fontSize: 14, marginTop: 20, marginBottom: 10 }}>
                    <Text>HLA INFORMATION</Text>
                </View>

                <View key={'hla-info-'} style={{ ...styles.row, borderTop: '0px solid #ccc', borderBottom: '0px solid #ccc' }}>
                    <View style={{ ...styles.tableCellLabel, width: '20%', textAlign: 'center' }}>
                        <Text>&nbsp;</Text>
                    </View>
                    <View style={{ ...styles.tableCellLabel, width: '80%', textAlign: 'center', borderBottom: '1px solid #ccc' }}>
                        <Text>Donor Antigens</Text>
                    </View>
                </View>

                <View style={{ ...styles.row, borderTop: '0px solid #ccc', borderBottom: '1px solid #ccc' }}>
                    <View style={{ ...styles.tableCellLabel, width: '20%', textAlign: 'center' }}>
                        <Text>HLA</Text>
                    </View>
                    <View style={{ ...styles.tableCellLabel, width: '20%', textAlign: 'center' }}>
                        <Text>1st LR</Text>
                    </View>
                    <View style={{ ...styles.tableCellLabel, width: '20%', textAlign: 'center' }}>
                        <Text>1st HR</Text>
                    </View>
                    <View style={{ ...styles.tableCellLabel, width: '20%', textAlign: 'center' }}>
                        <Text>2nd LR</Text>
                    </View>
                    <View style={{ ...styles.tableCellLabel, width: '20%', textAlign: 'center' }}>
                        <Text>2nd HR</Text>
                    </View>
                </View>

                {hlaDetails.map((item, index) =>
                    <View key={'hla-info-' + index} style={{ ...styles.row, borderTop: '0px solid #ccc', borderBottom: '1px solid #ccc' }}>
                        <View style={{ ...styles.tableCell, width: '20%', textAlign: 'center' }}>
                            <Text>{item.hla}</Text>
                        </View>
                        <View style={{ ...styles.tableCell, width: '20%', textAlign: 'center' }}>
                            <Text>{item.lr1}</Text>
                        </View>
                        <View style={{ ...styles.tableCell, width: '20%', textAlign: 'center' }}>
                            <Text>{item.hr1}</Text>
                        </View>
                        <View style={{ ...styles.tableCell, width: '20%', textAlign: 'center' }}>
                            <Text>{item.lr2}</Text>
                        </View>
                        <View style={{ ...styles.tableCell, width: '20%', textAlign: 'center' }}>
                            <Text>{item.hr2}</Text>
                        </View>
                    </View>
                )}
            </Page>
        </Document>
    );
}

export default DonorInfo;